import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  useThreadActions,
  useThreadState,
} from '../../../contexts/ThreadLoadingContext';
import { useNewThread } from '../../../contexts/NewThreadContext';
import { ReactComponent as PinboardIcon } from '../../../icons/pinboard.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/tabs/campaign.svg';
import { ReactComponent as TabCloseIcon } from '../../../icons/tabs/tab-close.svg';
import { ReactComponent as ChatBubbleIcon } from '../../../icons/chat-bubble.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../icons/arrow-left.svg';
import { ReactComponent as BinIcon } from '../../../icons/bin.svg';
import { ReactComponent as ShareIcon } from '../../../icons/share.svg';
import ShareViewsModal from './ShareViewsModal';
import ConfirmModal from '../../confirm-modals/ConfirmModal';
import {
  UIDSLComponent,
  UIDSLComponentProps,
  UIDSLComponentType,
  UpdateThreadCanvasViewRequest,
  ThreadLayoutType,
  BoundProDoc,
  SubThreadInfo,
  ProjectAppTab,
} from '@dreamai/types';
import { UIUpdate } from './UIComponentRenderer';
import { getAppLayoutConfig, APP_LAYOUT_CONFIGS } from './appLayoutConfig';
import AppLayoutAssetModal from './AppLayoutAssetModal';
import SalesManagerCRMWrapper from './SalesManagerCRMWrapper';
import SystemManagerWrapper from './SystemManagerWrapper';
import OnboardingWrapper from './OnboardingWrapper';
import InboundEmailWrapper from './InboundEmailWrapper';
import CompanyResearchWrapper from './CompanyResearchWrapper';
import ProjectAppBlockRenderer from './ProjectAppBlockRenderer';
import ProjectAppGeneratedPage from './ProjectAppGeneratedPage';
import ProjectAppEmptyState from './ProjectAppEmptyState';
import ProjectPlanView from './ProjectPlanView';
import ProjectAppResearchSection from './ProjectAppResearchSection';
import ProjectRulesPanel from './ProjectRulesPanel';
import ProjectAppLoadingIndicator from './ProjectAppLoadingIndicator';
import { useProjectAppLoading } from '../../../providers/ProjectAppViewLoadingProvider';
import ProjectAppTabSpinner from './ProjectAppTabSpinner';
import AgentStatusPill from './AgentStatusPill';
import { useStatusPillHidden } from '../../../contexts/StatusPillContext';
import './project-app-loading-indicator.scss';

// Define interfaces for CanvasAsset and CanvasAssetComponent based on project structure
interface CanvasAssetComponent {
  id: string;
  component_type: string;
  props: Record<string, any>;
}

interface CanvasAsset {
  id: string;
  thread_id: string;
  created_at: string;
  title?: string;
  components: CanvasAssetComponent[];
  pinned?: boolean;
}
import api from '../../../api';
import './canvas-assets.scss';

interface CanvasAssetsProps {
  threadId: string;
  layoutType?: ThreadLayoutType;
  discussingObject?: string;
  discussingObjectMatch?: Record<string, any>;
  isMainThread?: boolean;
  isChannelThread?: boolean;
  isOnboardingThread?: boolean;
  isInboundEmailThread?: boolean;
  agentType?: string;
  boundProDocs?: BoundProDoc[];
  subThreads?: SubThreadInfo[];
  projectApps?: ProjectAppTab[];
  threadDescription?: string;
  layoutLoading?: boolean;
  onHasAssets?: (hasAssets: boolean) => void;
  onMobileBack?: () => void;
  onMobileOpen?: () => void;
  hideMobileChatButton?: boolean;
  iconImageUrl?: string;
}

type ViewMode = 'canvas' | 'dreambox';

const CanvasAssets: React.FC<CanvasAssetsProps> = React.memo(
  ({
    threadId,
    layoutType = 'tab',
    discussingObject,
    discussingObjectMatch,
    isMainThread,
    isChannelThread,
    isOnboardingThread,
    isInboundEmailThread,
    agentType,
    boundProDocs,
    subThreads,
    projectApps,
    threadDescription,
    layoutLoading,
    onHasAssets,
    onMobileBack,
    onMobileOpen,
    hideMobileChatButton,
    iconImageUrl,
  }) => {
    const navigate = useNavigate();
    const [assets, setAssets] = useState<CanvasAsset[]>([]);
    const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const [viewMode, setViewMode] = useState<ViewMode>('canvas');
    const [lastCanvasViewTime, setLastCanvasViewTime] = useState<number>(
      Date.now(),
    );
    const [hasNewCanvasAssets, setHasNewCanvasAssets] =
      useState<boolean>(false);
    const [contextMenu, setContextMenu] = useState<{
      visible: boolean;
      x: number;
      y: number;
      assetId: string | null;
    }>({ visible: false, x: 0, y: 0, assetId: null });
    const [appLayoutModalOpen, setAppLayoutModalOpen] = useState<boolean>(true);
    const [showArchiveConfirm, setShowArchiveConfirm] =
      useState<boolean>(false);
    const [isArchiving, setIsArchiving] = useState<boolean>(false);
    const [showShareViewsModal, setShowShareViewsModal] =
      useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const canvasContentRef = useRef<HTMLDivElement>(null);
    const canvasAssetsRef = useRef<HTMLDivElement>(null);
    const tabsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const contextMenuRef = useRef<HTMLDivElement>(null);

    // Check if this is a Sales Assistant thread (dedicated CRM UI)
    const isSalesAssistantThread =
      agentType === 'Sales Assistant' || agentType === 'Sales Manager';

    // Check if this is a System Assistant thread (dedicated dashboard UI)
    const isSystemAssistantThread =
      agentType === 'System Assistant' || agentType === 'System Manager';

    // Check if this is an onboarding thread
    const isOnboarding = !!isOnboardingThread;

    // Get app layout config for the discussing object type
    const appLayoutConfig = getAppLayoutConfig(discussingObject);
    const isAppLayout = layoutType === 'app' && !!appLayoutConfig;

    // Dynamic tabs from app layout components (e.g. CampaignWrapper tells us tabs after loading)
    const [dynamicTabs, setDynamicTabs] = useState<
      { id: string; label: string }[]
    >([]);

    // Dynamic actions from app layout components
    const [dynamicActions, setDynamicActions] = useState<
      {
        id: string;
        icon: React.ReactNode;
        onClick: () => void;
        title?: string;
        disabled?: boolean;
      }[]
    >([]);

    // Callback for components to set their tabs dynamically
    const handleTabsReady = useCallback(
      (tabs: { id: string; label: string }[]) => {
        setDynamicTabs(tabs);
      },
      [],
    );

    // Callback for components to set their actions dynamically
    const handleActionsReady = useCallback(
      (
        actions: {
          id: string;
          icon: React.ReactNode;
          onClick: () => void;
          title?: string;
          disabled?: boolean;
        }[],
      ) => {
        setDynamicActions(actions);
      },
      [],
    );

    // Check if a wrapper component will render and set its own tabs
    // Matches the render logic in canvas-assets-content
    const hasActiveWrapper =
      isOnboarding ||
      isSalesAssistantThread ||
      isSystemAssistantThread ||
      isInboundEmailThread ||
      (isAppLayout && appLayoutConfig && discussingObjectMatch);

    // Reset dynamic tabs and actions when switching to a thread without a wrapper
    // Wrappers set their own tabs via useLayoutEffect, so we only reset when
    // no wrapper will handle tabs
    useEffect(() => {
      if (!hasActiveWrapper) {
        setDynamicTabs([]);
        setDynamicActions([]);
      }
    }, [threadId, hasActiveWrapper]);

    // Use dynamic tabs if available, otherwise fall back to config tabs
    const baseTabs =
      dynamicTabs.length > 0 ? dynamicTabs : appLayoutConfig?.tabs || [];

    // Bound ProDocs / ProSheets used to render as canvas tabs alongside
    // project app tabs. We've turned that off — bindings still exist in
    // the BE (and grant thread-level access to the doc), but they no
    // longer clutter the tab strip. Keep this as an empty array typed
    // like a tab so all the downstream selection / pending-tab logic
    // compiles unchanged; nothing will ever match it.
    const boundProDocTabs: Array<{
      id: string;
      label: string;
      proDocId: string;
      proDocType: 'doc' | 'sheet';
    }> = [];
    void boundProDocs;

    // Threads with a specific app layout (Autopilot, Campaign, etc.) are not project threads
    const hasSpecificAppLayout =
      !!discussingObject && !!APP_LAYOUT_CONFIGS[discussingObject];

    // Tasks vs projects: a "task thread" is a subthread (parentThreadId is
    // set, surfaced via the StatusPillHidden context which ChatInterface
    // flips on for tasks). Anything else that isn't a special wrapper
    // thread is treated as a "project thread". We have to distinguish the
    // two because tasks get a dedicated TaskStatusCard tab (status / ETA /
    // steps) while projects get the project homepage / generated views.
    const isSubThread = useStatusPillHidden();

    const isTaskThread =
      isSubThread &&
      !isMainThread &&
      !isChannelThread &&
      !isOnboarding &&
      !isSalesAssistantThread &&
      !isSystemAssistantThread &&
      !isInboundEmailThread &&
      !hasSpecificAppLayout;

    const isProjectThread =
      !isSubThread &&
      !isMainThread &&
      !isChannelThread &&
      !isOnboarding &&
      !isSalesAssistantThread &&
      !isSystemAssistantThread &&
      !isInboundEmailThread &&
      !hasSpecificAppLayout;

    // Detect generated-pages system: the single project app has one or more
    // generated HTML views. When present, views become tabs (one per slug) and
    // the legacy "Project Plan" tab is hidden — the generated home page IS the
    // project plan.
    const firstApp =
      projectApps && projectApps.length > 0 ? projectApps[0] : null;
    const hasGeneratedViews = !!firstApp?.views && firstApp.views.length > 0;
    const hasLegacyBlocks =
      !!firstApp && !hasGeneratedViews && (firstApp.blocks?.length ?? 0) > 0;

    // Generated views → tabs (new system).
    // If an app exists but has no views yet (transient state right after creation),
    // emit a single placeholder tab so the iframe renderer can show the
    // "page being generated" empty state instead of an empty canvas.
    const projectViewTabs =
      isProjectThread && hasGeneratedViews && firstApp
        ? firstApp.views!.map((v) => ({
            id: `pv:${firstApp.appId}:${v.slug}`,
            label: v.name,
            appId: firstApp.appId,
            viewSlug: v.slug,
          }))
        : isProjectThread && firstApp && !hasLegacyBlocks
          ? [
              {
                id: `pv:${firstApp.appId}:home`,
                label: firstApp.name || 'Home',
                appId: firstApp.appId,
                viewSlug: 'home',
              },
            ]
          : [];

    // Virtual "Home" tab — only when no project app exists at all.
    // Renders an empty-state placeholder telling the user this is where their
    // app will live. Once an app exists, the generated homepage replaces it.
    const projectPlanTab =
      isProjectThread && !firstApp
        ? [{ id: 'project-plan', label: 'Home' }]
        : [];

    // Task subthreads get a "Task" tab showing live status, ETA, and steps.
    // Rendered by ProjectPlanView, which switches to TaskStatusCard when
    // useStatusPillHidden() is true.
    const taskStatusTab = isTaskThread
      ? [{ id: 'task-status', label: 'Task' }]
      : [];

    // Documents tab — always present for project threads so research
    // artifacts (information_dump) are always discoverable even when
    // the PVA doesn't include them in the project plan.
    const documentsTab = isProjectThread
      ? [{ id: 'documents', label: 'Documents' }]
      : [];

    // Rules tab — surfaces the durable operating rules the agent has
    // recorded for this project. Read-only in v1; users mutate rules
    // by talking to the agent. Only present when there's a project app
    // (rules attach to the app).
    const rulesTab =
      isProjectThread && firstApp
        ? [
            {
              id: `rules:${firstApp.appId}`,
              label: 'Rules',
              appId: firstApp.appId,
            },
          ]
        : [];

    // Legacy project app tab (block-based system) — only for apps that have
    // legacy blocks but no generated views (true legacy threads).
    const projectAppTabs =
      hasLegacyBlocks && firstApp
        ? [
            {
              id: `pa:${firstApp.appId}`,
              label: firstApp.name,
              appId: firstApp.appId,
            },
          ]
        : [];

    // Check if we have an app layout with a discussing object that uses dynamic tabs
    const hasAppLayoutWithDiscussingObject =
      isAppLayout && appLayoutConfig && discussingObjectMatch;

    // Combine base tabs with bound ProDoc tabs and project app view tabs
    // BUT: if there's an app layout with discussing object and dynamic tabs haven't loaded yet,
    // don't show ProDoc tabs yet (they'll appear after main tabs load)
    const shouldShowBoundProDocTabs =
      !hasAppLayoutWithDiscussingObject || dynamicTabs.length > 0;
    // Special thread types have dedicated wrappers — don't mix in project app tabs
    const shouldShowProjectAppTabs =
      !isSalesAssistantThread && !isSystemAssistantThread;
    const effectiveTabs = [
      ...projectPlanTab,
      ...taskStatusTab,
      ...baseTabs,
      ...(shouldShowBoundProDocTabs ? boundProDocTabs : []),
      ...(shouldShowProjectAppTabs ? projectAppTabs : []),
      ...(shouldShowProjectAppTabs ? projectViewTabs : []),
      ...documentsTab,
      ...(shouldShowProjectAppTabs ? rulesTab : []),
    ];

    // Check if we should show the header
    const hasAppTabs = effectiveTabs.length > 0;

    // URL search params for tab persistence
    const [searchParams, setSearchParams] = useSearchParams();

    // Check if the current tab is a bound ProDoc tab (must be after searchParams declaration)
    // Use same fallback as app layouts: currentAppTab || effectiveTabs[0]?.id
    const currentAppTab = searchParams.get('app_tab');
    const effectiveAppTab = currentAppTab || effectiveTabs[0]?.id;

    // Only consider a ProDoc selected if:
    // 1. It matches the effective app tab, AND
    // 2. Either there's no app layout with discussing object (so ProDoc is the main content),
    //    OR the user explicitly selected this ProDoc tab via URL parameter
    const selectedBoundProDoc = boundProDocTabs.find(
      (tab) =>
        tab.id === effectiveAppTab &&
        (!hasAppLayoutWithDiscussingObject || currentAppTab === tab.id),
    );

    // Check if we're waiting for a potential ProDoc tab to load
    // This happens when:
    // 1. layoutLoading is true (boundProDocs not loaded yet), OR
    // 2. For app layouts with discussing objects: dynamicTabs haven't loaded yet
    //    (boundProDocTabs are hidden until dynamicTabs load)
    // AND there's an app_tab that looks like a UUID (potential ProDoc ID)
    // AND the tab isn't in effectiveTabs yet
    const isUuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const isWaitingForDynamicTabs =
      hasAppLayoutWithDiscussingObject && dynamicTabs.length === 0;
    const pendingProDocTab =
      (layoutLoading || isWaitingForDynamicTabs) &&
      currentAppTab &&
      isUuidPattern.test(currentAppTab) &&
      !effectiveTabs.some((tab) => tab.id === currentAppTab);

    // When app_tab is a ProDoc UUID but dynamicTabs haven't loaded yet,
    // we need to pass a default tabId to the wrapper so it can render and call onTabsReady.
    // Once dynamicTabs load, effectiveTabs will include the ProDoc and selectedBoundProDoc will find it.
    const isPendingProDocInAppLayout =
      hasAppLayoutWithDiscussingObject &&
      currentAppTab &&
      isUuidPattern.test(currentAppTab) &&
      dynamicTabs.length === 0;

    // Check if the current tab is a project app tab (legacy block-based)
    const selectedProjectApp = projectAppTabs.find(
      (tab) => tab.id === effectiveAppTab,
    );

    // Check if the current tab is a generated-view tab (new system)
    const selectedProjectView = projectViewTabs.find(
      (tab) => tab.id === effectiveAppTab,
    );

    // Check if the virtual "Project Plan" tab is selected (legacy fallback only)
    const isProjectPlanSelected =
      isProjectThread &&
      !hasGeneratedViews &&
      effectiveAppTab === 'project-plan';

    // Task subthread tab — shows TaskStatusCard via ProjectPlanView.
    const isTaskStatusSelected =
      isTaskThread && effectiveAppTab === 'task-status';

    // Documents tab — renders ProjectAppResearchSection for the project thread.
    const isDocumentsSelected =
      isProjectThread && effectiveAppTab === 'documents';

    // Rules tab — renders ProjectRulesPanel listing the project's operating rules.
    const selectedRulesTab = rulesTab.find((tab) => tab.id === effectiveAppTab);

    const showHeader =
      isOnboarding ||
      isSalesAssistantThread ||
      isSystemAssistantThread ||
      isInboundEmailThread ||
      isAppLayout ||
      isProjectThread ||
      isTaskThread ||
      selectedBoundProDoc ||
      selectedProjectApp
        ? hasAppTabs
        : assets.length > 0;

    // Track when user switches to canvas view to reset new asset flag
    useEffect(() => {
      if (viewMode === 'canvas') {
        setLastCanvasViewTime(Date.now());
        setHasNewCanvasAssets(false);
      }
    }, [viewMode]);

    // Check for new canvas assets since last canvas view
    useEffect(() => {
      if (assets.length > 0 && viewMode === 'dreambox') {
        const hasNewAssets = assets.some((asset) => {
          const assetTime = new Date(asset.created_at).getTime();
          return assetTime > lastCanvasViewTime;
        });
        setHasNewCanvasAssets(hasNewAssets);
      }
    }, [assets, lastCanvasViewTime, viewMode]);

    // Assets that are NOT pure ConfirmationCarousel — those are handled inline, not via modal
    const nonConfirmationAssets = assets.filter(
      (a) =>
        !a.components.every((c) => c.component_type === 'ConfirmationCarousel'),
    );

    // Reopen modal when new non-confirmation assets arrive in app layout mode
    useEffect(() => {
      if (isAppLayout && nonConfirmationAssets.length > 0) {
        setAppLayoutModalOpen(true);
      }
    }, [isAppLayout, nonConfirmationAssets.length]);

    // Notify parent when there's content to display (assets or app layout content)
    useEffect(() => {
      const hasAppContent = (isAppLayout || isProjectThread) && hasAppTabs;
      const hasContent = assets.length > 0 || hasAppContent;
      onHasAssets?.(hasContent);
    }, [assets.length, isAppLayout, isProjectThread, hasAppTabs, onHasAssets]);

    // Animation states
    const [isAnimating, setIsAnimating] = useState<boolean>(false);
    const [animationStage, setAnimationStage] = useState<
      'fadeOut' | 'fadeIn' | 'none'
    >('none');
    const [prevAssetId, setPrevAssetId] = useState<string | null>(null);

    // Animation timing constants
    const FADE_OUT_DURATION = 50;
    const FADE_IN_DURATION = 50;

    // Function to scroll tab into view
    const scrollTabIntoView = useCallback((assetId: string) => {
      const tabElement = tabsRef.current[assetId];
      if (tabElement) {
        tabElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }, []);

    // Function to handle asset transitions with animation
    const animateAssetTransition = useCallback(
      (newAssetId: string, currentAssetId: string | null) => {
        // Animation disabled for now
        setSelectedAssetId(newAssetId);
        scrollTabIntoView(newAssetId);

        // if (currentAssetId) {
        //   // If we have an existing asset, do a fade out first
        //   setPrevAssetId(currentAssetId);
        //   setIsAnimating(true);
        //   setAnimationStage('fadeOut');

        //   // After fadeOut completes, switch to the new asset and start fadeIn
        //   setTimeout(() => {
        //     setSelectedAssetId(newAssetId);
        //     setAnimationStage('fadeIn');

        //     // Scroll the new tab into view
        //     scrollTabIntoView(newAssetId);

        //     // After fadeIn completes, reset animation states
        //     setTimeout(() => {
        //       setIsAnimating(false);
        //       setAnimationStage('none');
        //       setPrevAssetId(null);
        //     }, FADE_IN_DURATION);
        //   }, FADE_OUT_DURATION);
        // } else {
        //   // No existing asset, just set the new one directly
        //   setSelectedAssetId(newAssetId);
        //   // Scroll the tab into view
        //   scrollTabIntoView(newAssetId);
        // }
      },
      [scrollTabIntoView],
    );

    const {
      registerCanvasAssetCreatedCallback,
      unregisterCanvasAssetCreatedCallback,
      registerCanvasAssetRemovedCallback,
      unregisterCanvasAssetRemovedCallback,
      registerCanvasComponentUpdatedCallback,
      unregisterCanvasComponentUpdatedCallback,
      registerConfettiCallback,
      unregisterConfettiCallback,
    } = useThreadActions();
    const { loadingThreads } = useThreadState();
    const { loadingApp } = useProjectAppLoading();
    const { newThreadId } = useNewThread();

    // Use the appropriate thread ID (existing or new)
    const effectiveThreadId = threadId || newThreadId;

    const { getAccessTokenSilently } = useAuth0();

    // Archive the current project thread. We stay on the same URL after
    // archiving — ThreadView reads `is_archived_thread` from the thread fetch
    // and swaps the input area for an "archived" notice. ThreadViewContainer
    // only fetches that data on threadId change, so we trigger a reload here
    // to refresh it in place. The Projects sidebar drawer refetches on open
    // and filters out archived threads, so it'll drop the project the next
    // time it's opened.
    const handleArchiveProject = useCallback(async () => {
      if (!threadId) return;
      setIsArchiving(true);
      try {
        const token = await getAccessTokenSilently();
        await api.post(
          '/agents/threads/archive',
          { threadId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setShowArchiveConfirm(false);
        window.location.reload();
      } catch (error: any) {
        console.error('Error archiving project:', error);
        alert(error.response?.data?.error || 'Failed to archive project');
      } finally {
        setIsArchiving(false);
      }
    }, [threadId, getAccessTokenSilently]);

    // Handler to unbind a ProDoc from the thread
    const handleUnbindProDoc = useCallback(
      async (proDocId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
          const token = await getAccessTokenSilently();
          await api.delete(
            `/agents/threads/${threadId}/bound-prodocs/${proDocId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
        } catch (error) {
          console.error('Failed to unbind ProDoc:', error);
        }
      },
      [threadId, getAccessTokenSilently],
    );

    // Batch delete canvas assets
    // Close context menu when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          contextMenuRef.current &&
          !contextMenuRef.current.contains(event.target as Node)
        ) {
          setContextMenu({ visible: false, x: 0, y: 0, assetId: null });
        }
      };

      if (contextMenu.visible) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
    }, [contextMenu.visible]);

    const deleteCanvasAssets = useCallback(
      async (assetIds: string[]) => {
        const deletableAssets = assets.filter((asset) =>
          assetIds.includes(asset.id),
        );

        if (deletableAssets.length === 0) {
          return { success: false, error: 'No deletable assets found' };
        }

        const deletableIds = deletableAssets.map((a) => a.id);

        // Track deleted assets to prevent them from being restored by fetch
        deletableIds.forEach((id) => deletedAssetIdsRef.current.add(id));

        // Optimistically remove from UI
        const previousAssets = [...assets];
        const previousSelectedAssetId = selectedAssetId;
        setAssets((prev) => prev.filter((a) => !deletableIds.includes(a.id)));

        // If the selected asset is being deleted, switch to another tab
        if (selectedAssetId && deletableIds.includes(selectedAssetId)) {
          const remainingAssets = assets.filter(
            (a) => !deletableIds.includes(a.id),
          );
          if (remainingAssets.length > 0) {
            const newAssetId = remainingAssets[0].id;
            setSelectedAssetId(newAssetId);
            // Update URL to reflect the new selected asset
            setSearchParams((prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set('asset_id', newAssetId);
              return newParams;
            });
          } else {
            setSelectedAssetId(null);
            // Remove asset_id from URL when no assets left
            setSearchParams((prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.delete('asset_id');
              return newParams;
            });
          }
        }

        try {
          const token = await getAccessTokenSilently();
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          const response = await api.delete('/canvas-assets', {
            data: { assetIds: deletableIds },
          });

          if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to delete assets');
          }

          return { success: true, deletedIds: deletableIds };
        } catch (error: any) {
          console.error('Error deleting canvas assets:', error);
          // Rollback on error - restore both assets and selected tab
          setAssets(previousAssets);
          setSelectedAssetId(previousSelectedAssetId);
          return {
            success: false,
            error: error.response?.data?.error || 'Failed to delete assets',
          };
        }
      },
      [assets, selectedAssetId, getAccessTokenSilently, setSearchParams],
    );

    // Use refs to store current values to avoid dependency issues
    const effectiveThreadIdRef = useRef(effectiveThreadId);
    const assetsRef = useRef(assets);
    const selectedAssetIdRef = useRef(selectedAssetId);
    const deletedAssetIdsRef = useRef<Set<string>>(new Set());
    const searchParamsRef = useRef(searchParams);
    const switchAssetItemRef = useRef<(newAssetId: string) => void>(() => {});

    // Update refs when values change
    useEffect(() => {
      effectiveThreadIdRef.current = effectiveThreadId;
    }, [effectiveThreadId]);

    useEffect(() => {
      assetsRef.current = assets;
    }, [assets]);

    useEffect(() => {
      selectedAssetIdRef.current = selectedAssetId;
    }, [selectedAssetId]);

    useEffect(() => {
      searchParamsRef.current = searchParams;
    }, [searchParams]);

    // This ref is updated after switchAssetItem is defined below

    // Generate a unique callback ID for this component instance
    const callbackIdRef = useRef(
      `canvas-assets-component-${Math.random().toString(36).substr(2, 9)}`,
    );

    // Function to update the thread's visual description
    const updateThreadViewVisualDescription = useCallback(
      async (visualDescription: string) => {
        if (!effectiveThreadId || !selectedAssetId) return;

        try {
          const token = await getAccessTokenSilently();
          const requestBody: UpdateThreadCanvasViewRequest = {
            threadId: effectiveThreadId,
            canvasAssetId: selectedAssetId,
            visualDescription,
          };

          await api.put('/canvas-assets/thread-view', requestBody, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          console.log('Updated thread visual description:', visualDescription);
        } catch (error) {
          console.error('Error updating thread visual description:', error);
        }
      },
      [effectiveThreadId, selectedAssetId, getAccessTokenSilently],
    );

    // Function to update the thread's current canvas view in the backend
    const updateThreadCanvasView = useCallback(
      async (assetId: string) => {
        if (!effectiveThreadId) return;

        try {
          const token = await getAccessTokenSilently();
          const payload: UpdateThreadCanvasViewRequest = {
            threadId: effectiveThreadId,
            canvasAssetId: assetId,
            // We could also add a visual description here if needed
            visualDescription: `Showing the main canvas item`,
          };

          await api.put('/canvas-assets/thread-view', payload, {
            headers: { Authorization: `Bearer ${token}` },
          });

          // We don't need to do anything with the response
          // If it fails, we still want the UI to update
        } catch (error) {
          // Log the error but don't prevent the UI from updating
          console.error('Error updating thread canvas view:', error);
        }
      },
      [effectiveThreadId, getAccessTokenSilently],
    );

    // Combined function to switch asset items - handles both animation and API update
    const switchAssetItem = useCallback(
      (newAssetId: string) => {
        // Don't switch if we don't have a thread ID yet
        if (!effectiveThreadId) {
          return;
        }

        // Don't switch if already on this asset (prevents loops)
        if (selectedAssetIdRef.current === newAssetId) {
          return;
        }

        // Don't switch to deleted assets
        if (deletedAssetIdsRef.current.has(newAssetId)) {
          return;
        }

        // Start the animation using ref to avoid dependency cycle
        animateAssetTransition(newAssetId, selectedAssetIdRef.current);

        // Update the backend (don't wait for it to complete)
        updateThreadCanvasView(newAssetId);

        // Only update URL parameter if thread_id is in the URL
        // This prevents triggering NewThreadContext clearing when on new=true pages
        // Use ref to get current searchParams value to avoid stale closure
        const urlThreadId = searchParamsRef.current.get('thread_id');
        if (urlThreadId) {
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set('asset_id', newAssetId);
            return newParams;
          });
        }
      },
      [
        animateAssetTransition,
        updateThreadCanvasView,
        effectiveThreadId,
        setSearchParams,
      ],
    );

    // Keep ref updated with latest switchAssetItem
    useEffect(() => {
      switchAssetItemRef.current = switchAssetItem;
    }, [switchAssetItem]);

    // Fetch all canvas assets for the thread
    const fetchCanvasAssets = useCallback(async () => {
      if (!effectiveThreadId) return;

      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const response = await api.get(
          `/canvas-assets/thread/${effectiveThreadId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const fetchedAssets = response.data.assets || [];

        // Sort assets: pinned first (oldest pinned first), then unpinned (oldest first)
        const sortedAssets = fetchedAssets.sort(
          (a: CanvasAsset, b: CanvasAsset) => {
            // If one is pinned and the other isn't, pinned comes first
            if (a.pinned && !b.pinned) return -1;
            if (!a.pinned && b.pinned) return 1;

            // If both have same pinned status, sort by created_at (oldest first)
            return (
              new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime()
            );
          },
        );

        // Filter out any assets that were just deleted (backend might not have processed yet)
        const filteredAssets = sortedAssets.filter(
          (a: CanvasAsset) => !deletedAssetIdsRef.current.has(a.id),
        );

        setAssets(filteredAssets);

        // Check URL parameter for asset selection
        const urlAssetId = searchParams.get('asset_id');

        if (
          urlAssetId &&
          filteredAssets.some((a: CanvasAsset) => a.id === urlAssetId)
        ) {
          // URL has valid asset_id, use it
          setSelectedAssetId(urlAssetId);
          updateThreadCanvasView(urlAssetId);
        } else if (filteredAssets.length > 0 && !selectedAssetId) {
          // No URL param or invalid, select first asset
          const newAssetId = filteredAssets[0].id;
          setSelectedAssetId(newAssetId);
          updateThreadCanvasView(newAssetId);
          // Set URL parameter
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set('asset_id', newAssetId);
            return newParams;
          });
        }
      } catch (error) {
        console.error('Error fetching canvas assets:', error);
      } finally {
        setLoading(false);
      }
    }, [
      effectiveThreadId,
      // Removed selectedAssetId and searchParams to prevent infinite loop
      // These are read from state/URL but don't need to trigger re-fetch
      updateThreadCanvasView,
      getAccessTokenSilently,
      setSearchParams,
    ]);

    // Handle new canvas asset created event
    const handleCanvasAssetCreated = useCallback(
      async (threadId: string, data: { canvasAssetId: string }) => {
        if (threadId !== effectiveThreadIdRef.current) {
          return;
        }

        // Check if asset already exists to prevent duplicates
        if (
          assetsRef.current.some((asset) => asset.id === data.canvasAssetId)
        ) {
          return;
        }

        try {
          const token = await getAccessTokenSilently();
          const response = await api.get(
            `/canvas-assets/${data.canvasAssetId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          const newAsset = response.data.asset;

          setAssets((prevAssets) => {
            // Don't add if it's in the deleted list
            if (deletedAssetIdsRef.current.has(newAsset.id)) {
              return prevAssets;
            }

            // Double-check for duplicates in case of race conditions
            if (prevAssets.some((asset) => asset.id === newAsset.id)) {
              return prevAssets;
            }

            // Add at the end to match the "oldest first" sorting order
            return [...prevAssets, newAsset];
          });

          // Switch to the new asset (handles both animation and backend update)
          // Only switch if not deleted
          // Use ref to always get the latest switchAssetItem function
          if (!deletedAssetIdsRef.current.has(newAsset.id)) {
            switchAssetItemRef.current(newAsset.id);
          }
        } catch (error) {
          console.error('Error fetching new canvas asset:', error);
        }
      },
      [getAccessTokenSilently],
    );

    // Initial fetch when thread changes
    // Note: Only depends on effectiveThreadId, not fetchCanvasAssets, to avoid infinite loops
    useEffect(() => {
      if (!effectiveThreadId) return;
      fetchCanvasAssets();
    }, [effectiveThreadId]);

    // Handle canvas asset removed event
    const handleCanvasAssetRemoved = useCallback(
      (threadId: string, data: { canvasAssetId: string }) => {
        // Only handle if this is for our thread
        if (threadId !== effectiveThreadIdRef.current) {
          return;
        }

        console.log('Canvas asset removed:', data.canvasAssetId);

        // Remove the asset from the list
        setAssets((prevAssets) => {
          const filtered = prevAssets.filter(
            (asset) => asset.id !== data.canvasAssetId,
          );

          // If the removed asset was selected, switch to the first available asset
          // Use refs to always get latest values
          if (
            selectedAssetIdRef.current === data.canvasAssetId &&
            filtered.length > 0
          ) {
            switchAssetItemRef.current(filtered[0].id);
          }

          return filtered;
        });
      },
      [],
    );

    // Confetti handler
    const handleConfetti = useCallback(async (threadId: string, data: any) => {
      const confetti = (await import('canvas-confetti')).default;
      confetti({
        particleCount: 400,
        spread: 170,
        origin: { y: 0.6 },
      });
    }, []);

    // Handle canvas component updated event
    const handleCanvasComponentUpdated = useCallback(
      (
        threadId: string,
        data: {
          canvasAssetId: string;
          componentId: string;
          props: Record<string, unknown>;
        },
      ) => {
        if (threadId !== effectiveThreadIdRef.current) {
          return;
        }

        console.log('Canvas component updated:', data);

        // Update the component props in the asset
        setAssets((prevAssets) =>
          prevAssets.map((asset) => {
            if (asset.id !== data.canvasAssetId) {
              return asset;
            }

            // Update the component within this asset
            const updatedComponents = asset.components.map((component: any) => {
              if (component.id !== data.componentId) {
                return component;
              }
              return { ...component, props: data.props };
            });

            return { ...asset, components: updatedComponents };
          }),
        );
      },
      [],
    );

    // Register and unregister callbacks
    useEffect(() => {
      if (!effectiveThreadId) return;

      // Register callback for new canvas assets using unique ID
      registerCanvasAssetCreatedCallback(
        callbackIdRef.current,
        handleCanvasAssetCreated,
      );

      // Register callback for removed canvas assets
      registerCanvasAssetRemovedCallback(
        callbackIdRef.current,
        handleCanvasAssetRemoved,
      );

      // Register callback for confetti
      registerConfettiCallback(callbackIdRef.current, handleConfetti);

      // Register callback for canvas component updates
      registerCanvasComponentUpdatedCallback(
        callbackIdRef.current,
        handleCanvasComponentUpdated,
      );

      return () => {
        unregisterCanvasAssetCreatedCallback(callbackIdRef.current);
        unregisterCanvasAssetRemovedCallback(callbackIdRef.current);
        unregisterConfettiCallback(callbackIdRef.current);
        unregisterCanvasComponentUpdatedCallback(callbackIdRef.current);
      };
      // Note: Intentionally excluding callback functions from dependencies
      // They use refs internally for thread safety, and including them causes
      // excessive re-registration when state changes (e.g., selectedAssetId)
    }, [
      effectiveThreadId,
      registerCanvasAssetCreatedCallback,
      unregisterCanvasAssetCreatedCallback,
      registerCanvasAssetRemovedCallback,
      unregisterCanvasAssetRemovedCallback,
      registerConfettiCallback,
      unregisterConfettiCallback,
      registerCanvasComponentUpdatedCallback,
      unregisterCanvasComponentUpdatedCallback,
    ]);

    // Get the currently selected asset
    const selectedAsset = assets.find((asset) => asset.id === selectedAssetId);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setDropdownOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Get the title of the currently selected asset or default to "Pinboard"
    const getCurrentTitle = () => {
      if (loading) return 'Loading...';
      if (assets.length === 0) return 'Pinboard';
      if (!selectedAssetId) return 'Pinboard';

      const current = assets.find((asset) => asset.id === selectedAssetId);
      return current?.title || `Asset ${selectedAssetId.substring(0, 8)}`;
    };

    return (
      <div
        className={`canvas-assets${!hasAppTabs ? ' just-backdrop-only' : ''}`}
        ref={canvasAssetsRef}
      >
        {showHeader && (
          <div
            className={`canvas-assets-header${isProjectThread ? ' project-thread' : ''}${hideMobileChatButton && !isProjectThread ? ' task-thread' : ''}`}
            ref={dropdownRef}
          >
            {/* Mobile task nav bar — back to task list, plus an "open
                discussion" button only when a chat drawer is actually
                wired up via onMobileBack. Task threads (which no longer
                have a chat sidebar) don't pass onMobileBack to the main
                canvas, so the chat bubble disappears for them. */}
            {(isProjectThread || hideMobileChatButton) && (
              <div className='mobile-task-nav'>
                <button
                  className='mobile-task-nav-btn'
                  onClick={() => {
                    onMobileBack?.();
                    navigate(-1);
                  }}
                  aria-label='Back to tasks'
                >
                  <ArrowLeftIcon />
                </button>
                {onMobileBack && (
                  <button
                    className='mobile-task-nav-btn open-discussion'
                    onClick={() => onMobileBack?.()}
                    aria-label='Open discussion'
                  >
                    <ChatBubbleIcon />
                  </button>
                )}
              </div>
            )}
            <div className='canvas-header-row'>
              <div className='canvas-header-top'>
                <div className='thread-image-container'>
                  {isProjectThread ? (
                    <img
                      src={`/splicons/project-tags/${discussingObjectMatch?.projectIcon || 'other'}.png`}
                      alt='Project icon'
                      className='thread-image thread-image-actual'
                    />
                  ) : (
                    <>
                      <img
                        src='/splicons/task-default.png'
                        alt=''
                        className={`thread-image thread-image-placeholder${iconImageUrl ? ' faded' : ''}`}
                      />
                      {iconImageUrl && (
                        <img
                          src={iconImageUrl}
                          alt='Thread icon'
                          className='thread-image thread-image-actual'
                        />
                      )}
                    </>
                  )}
                </div>
                <div className='canvas-header-stack'>
                  {((threadDescription &&
                    (isProjectThread || hideMobileChatButton)) ||
                    (isProjectThread && threadId)) && (
                    <div className='canvas-header-title-row'>
                      {threadDescription &&
                        (isProjectThread || hideMobileChatButton) && (
                          <h1 className='canvas-header-project-title'>
                            {threadDescription}
                          </h1>
                        )}
                      {isProjectThread && threadId && hasGeneratedViews && (
                        <div className='project-share-wrapper'>
                          <button
                            className='project-share-button'
                            onClick={() =>
                              setShowShareViewsModal((prev) => !prev)
                            }
                            title='Share views'
                          >
                            <ShareIcon />
                          </button>
                          {firstApp && (
                            <ShareViewsModal
                              show={showShareViewsModal}
                              onClose={() => setShowShareViewsModal(false)}
                              appId={firstApp.appId}
                              views={firstApp.views!.map((v) => ({
                                slug: v.slug,
                                name: v.name,
                              }))}
                              activeViewSlug={selectedProjectView?.viewSlug}
                            />
                          )}
                        </div>
                      )}
                      {isProjectThread && threadId && (
                        <button
                          className='project-archive-button'
                          onClick={() => setShowArchiveConfirm(true)}
                          title='Archive'
                          disabled={isArchiving}
                        >
                          <BinIcon />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {viewMode === 'dreambox' ? (
              <div className='canvas-assets-title'>
                <PinboardIcon />
                <span>Dreambox</span>
              </div>
            ) : isOnboarding ||
              isSalesAssistantThread ||
              isSystemAssistantThread ||
              isInboundEmailThread ||
              isProjectThread ||
              (isAppLayout && appLayoutConfig) ? (
              // App layout, Sales Manager main thread, or Onboarding: tabs from dynamic tabs
              <div className='tabs app-layout'>
                <div className='tabs-inner'>
                  {/* Mobile back button to return to chat */}
                  {onMobileBack && !hideMobileChatButton && (
                    <button
                      className='mobile-back-btn'
                      onClick={onMobileBack}
                      aria-label='Back to chat'
                    >
                      <ChatBubbleIcon />
                    </button>
                  )}
                  {effectiveTabs.map((tab) => {
                    const activeAppTab =
                      searchParams.get('app_tab') ||
                      effectiveTabs[0]?.id ||
                      'companies';
                    const isBoundProDoc = 'proDocId' in tab;
                    return (
                      <div
                        key={tab.id}
                        className={`tab ${activeAppTab === tab.id ? 'active' : ''}`}
                        onClick={() => {
                          setSearchParams(
                            (prev) => {
                              const newParams = new URLSearchParams(prev);
                              newParams.set('app_tab', tab.id);
                              return newParams;
                            },
                            { replace: true },
                          );
                        }}
                      >
                        <div className='tab-content'>
                          {tab.id === 'overview' && <CampaignIcon />}
                          <span className='tab-text'>{tab.label}</span>
                          {'appId' in tab && (
                            <ProjectAppTabSpinner appId={(tab as any).appId} />
                          )}
                          {isBoundProDoc && (
                            <button
                              className='tab-close-button'
                              onClick={(e) =>
                                handleUnbindProDoc(
                                  (tab as { proDocId: string }).proDocId,
                                  e,
                                )
                              }
                              aria-label='Close tab'
                            >
                              <TabCloseIcon />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className='tab-right-section'>
                  {discussingObject === 'CompanyResearch' &&
                    effectiveThreadId && (
                      <AgentStatusPill threadId={effectiveThreadId} />
                    )}
                  {dynamicActions.length > 0 && (
                    <div className='tab-actions'>
                      {dynamicActions.map((action) => (
                        <button
                          key={action.id}
                          className='tab-action-button'
                          onClick={() => {
                            onMobileOpen?.();
                            action.onClick?.();
                          }}
                          title={action.title}
                          disabled={action.disabled}
                        >
                          {action.icon}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Tab layout: dynamic canvas asset tabs
              <div className='tabs'>
                <div className='tabs-inner'>
                  {assets.map((asset) => (
                    <div
                      key={asset.id}
                      ref={(el) => {
                        tabsRef.current[asset.id] = el;
                      }}
                      className={`tab ${
                        asset.id === selectedAssetId ? 'active' : ''
                      }`}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu({
                          visible: true,
                          x: e.clientX,
                          y: e.clientY,
                          assetId: asset.id,
                        });
                      }}
                    >
                      <div
                        className='tab-content'
                        onClick={() => {
                          if (asset.id !== selectedAssetId) {
                            switchAssetItem(asset.id);
                          }
                        }}
                      >
                        {asset.title === 'Campaign overview' && (
                          <CampaignIcon />
                        )}
                        <span className='tab-text'>
                          {asset.title || `Asset ${asset.id.substring(0, 8)}`}
                        </span>
                      </div>
                      {!asset.pinned && (
                        <div
                          className='tab-close'
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCanvasAssets([asset.id]);
                          }}
                          aria-label='Close tab'
                        >
                          <TabCloseIcon />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Context Menu */}
        {contextMenu.visible && contextMenu.assetId && (
          <div
            ref={contextMenuRef}
            className='tab-context-menu'
            style={{
              position: 'fixed',
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`,
              zIndex: 1000,
            }}
          >
            <div
              className='context-menu-item'
              onClick={() => {
                if (
                  contextMenu.assetId &&
                  contextMenu.assetId !== selectedAssetId
                ) {
                  switchAssetItem(contextMenu.assetId);
                }
                setContextMenu({ visible: false, x: 0, y: 0, assetId: null });
              }}
            >
              Open Tab
            </div>
            {(() => {
              const asset = assets.find((a) => a.id === contextMenu.assetId);
              return !asset?.pinned ? (
                <div
                  className='context-menu-item'
                  onClick={() => {
                    if (contextMenu.assetId) {
                      deleteCanvasAssets([contextMenu.assetId]);
                    }
                    setContextMenu({
                      visible: false,
                      x: 0,
                      y: 0,
                      assetId: null,
                    });
                  }}
                >
                  Close Tab
                </div>
              ) : null;
            })()}

            <div className='context-menu-divider' />
            <div
              className='context-menu-item'
              onClick={() => {
                const currentIndex = assets.findIndex(
                  (a) => a.id === contextMenu.assetId,
                );
                const otherAssets = assets.filter(
                  (a, idx) => idx !== currentIndex && !a.pinned,
                );
                deleteCanvasAssets(otherAssets.map((a) => a.id));
                setContextMenu({ visible: false, x: 0, y: 0, assetId: null });
              }}
            >
              Close Other Tabs
            </div>
            <div
              className='context-menu-item'
              onClick={() => {
                const currentIndex = assets.findIndex(
                  (a) => a.id === contextMenu.assetId,
                );
                const tabsToRight = assets
                  .slice(currentIndex + 1)
                  .filter((a) => !a.pinned);
                deleteCanvasAssets(tabsToRight.map((a) => a.id));
                setContextMenu({ visible: false, x: 0, y: 0, assetId: null });
              }}
            >
              Close Tabs to the Right
            </div>
          </div>
        )}

        <div className='canvas-assets-content' ref={canvasContentRef}>
          {pendingProDocTab && !hasAppLayoutWithDiscussingObject ? (
            // Waiting for bound ProDocs to load (app_tab is a UUID that might be a ProDoc)
            // Only show loading for non-app-layout threads; app layouts need to render their wrapper
            <div className='canvas-loading'>
              <div className='loading-spinner' />
              <p>Loading...</p>
            </div>
          ) : selectedBoundProDoc && !isPendingProDocInAppLayout ? (
            // Bound ProDoc tab selected AND dynamic tabs have loaded: render CompanyResearchWrapper
            // (If isPendingProDocInAppLayout is true, we need to let the wrapper render first to get tabs)
            <CompanyResearchWrapper
              key={`${effectiveThreadId}-${selectedBoundProDoc.proDocId}`}
              proDocId={selectedBoundProDoc.proDocId}
              mode={
                selectedBoundProDoc.proDocType === 'sheet'
                  ? 'multiple'
                  : 'single'
              }
              threadId={effectiveThreadId || undefined}
              tabId='research'
              updateThreadViewVisualDescription={
                updateThreadViewVisualDescription
              }
            />
          ) : isOnboarding ? (
            // Onboarding thread: render onboarding wrapper
            <OnboardingWrapper
              key={effectiveThreadId}
              threadId={effectiveThreadId || undefined}
              tabId={searchParams.get('app_tab') || 'getting-to-know-you'}
              onTabsReady={handleTabsReady}
            />
          ) : isInboundEmailThread ? (
            // Inbound email thread: render email tagging settings
            <InboundEmailWrapper
              key={effectiveThreadId}
              threadId={effectiveThreadId || undefined}
              tabId={searchParams.get('app_tab') || 'email-tagging'}
              onTabsReady={handleTabsReady}
            />
          ) : isAppLayout &&
            appLayoutConfig &&
            discussingObjectMatch &&
            hasSpecificAppLayout ? (
            // App layout (Campaign, CompanyResearch, etc.): render component based on config.
            // Only when a SPECIFIC layout config exists — the default config's
            // renderComponent returns null, and letting it win here would
            // swallow the project/task tab content below (blank canvas).
            // If app_tab is a ProDoc UUID and dynamicTabs haven't loaded yet,
            // pass 'overview' as default so the wrapper can render and call onTabsReady
            appLayoutConfig.renderComponent({
              tabId: isPendingProDocInAppLayout
                ? 'overview'
                : searchParams.get('app_tab') ||
                  effectiveTabs[0]?.id ||
                  'overview',
              threadId: effectiveThreadId || undefined,
              discussingObjectMatch: discussingObjectMatch || {},
              updateThreadViewVisualDescription,
              onTabsReady: handleTabsReady,
              onActionsReady: handleActionsReady,
            })
          ) : selectedProjectView ? (
            // Generated view tab selected: render the iframe-based generated page
            <ProjectAppGeneratedPage
              key={`${effectiveThreadId}-${selectedProjectView.appId}-${selectedProjectView.viewSlug}`}
              appId={selectedProjectView.appId}
              slug={selectedProjectView.viewSlug}
            />
          ) : isDocumentsSelected ? (
            // Documents tab: show all information_dump artifacts for this
            // project thread (created by the thread or any of its children).
            <ProjectAppResearchSection
              key={`${effectiveThreadId}-documents`}
              threadId={effectiveThreadId || null}
              headingLabel='Documents'
              showEmptyState
            />
          ) : selectedRulesTab ? (
            // Rules tab: list the project's operating rules (read-only).
            <ProjectRulesPanel
              key={`${effectiveThreadId}-${selectedRulesTab.appId}-rules`}
              appId={selectedRulesTab.appId}
            />
          ) : isProjectPlanSelected ? (
            // Project thread without a project app yet: show empty-state
            // placeholder indicating this is where the homepage will live.
            // "Building" copy when a PVA run is active or the thread is
            // currently processing a message.
            <ProjectAppEmptyState
              key={`${effectiveThreadId}-project-empty`}
              building={
                !!loadingApp ||
                !!(effectiveThreadId && loadingThreads[effectiveThreadId])
              }
            />
          ) : isTaskStatusSelected ? (
            // Task subthread: render the task status card (status / ETA /
            // steps) plus any research documents. ProjectPlanView reads
            // useStatusPillHidden() to switch into its task layout.
            <ProjectPlanView
              key={`${effectiveThreadId}-task-status`}
              threadId={effectiveThreadId || null}
              discussingObject={discussingObject}
              threadDescription={threadDescription}
            />
          ) : selectedProjectApp ? (
            // Project app tab selected: render blocks (legacy block-based system)
            <div className='project-app-blocks-container'>
              <ProjectAppBlockRenderer
                key={`${effectiveThreadId}-${selectedProjectApp.appId}`}
                appId={selectedProjectApp.appId}
              />
            </div>
          ) : isSalesAssistantThread ? (
            // Sales Assistant fallback: render CRM wrapper (when no specific app layout)
            <SalesManagerCRMWrapper
              key={effectiveThreadId}
              threadId={effectiveThreadId || undefined}
              tabId={searchParams.get('app_tab') || 'companies'}
              updateThreadViewVisualDescription={
                updateThreadViewVisualDescription
              }
              onTabsReady={handleTabsReady}
            />
          ) : isSystemAssistantThread ? (
            // System Assistant fallback: render dashboard wrapper (when no specific app layout)
            <SystemManagerWrapper
              key={effectiveThreadId}
              threadId={effectiveThreadId || undefined}
              tabId={searchParams.get('app_tab') || 'activity'}
              updateThreadViewVisualDescription={
                updateThreadViewVisualDescription
              }
              onTabsReady={handleTabsReady}
            />
          ) : isAppLayout ? null : viewMode === 'canvas' ? ( // Default app layout: don't render anything, only use modal
            assets.length === 0 ? null : (
              selectedAsset &&
              selectedAsset.components && (
                <div
                  className={`canvas-asset-wrapper ${isAnimating ? 'animating' : ''} ${animationStage}`}
                  style={{ position: 'relative' }}
                >
                  {selectedAsset.components.map(
                    (component: CanvasAssetComponent, index: number) => {
                      // Map the component to a UIDSLComponent
                      const uiComponent: UIDSLComponent = {
                        type: component.component_type as UIDSLComponentType,
                        id: component.id,
                        props: {
                          ...component.props,
                          componentId: component.id,
                        } as unknown as UIDSLComponentProps,
                      };

                      return (
                        <UIUpdate
                          component={uiComponent}
                          viewId={selectedAsset.id}
                          threadId={effectiveThreadId}
                          section='canvas-assets'
                          onUserInput={() => {}}
                          updateThreadViewVisualDescription={
                            updateThreadViewVisualDescription
                          }
                          key={`${selectedAsset.id}-component-${index}`}
                        />
                      );
                    },
                  )}
                </div>
              )
            )
          ) : null}
        </div>
        {selectedProjectApp && (
          <ProjectAppLoadingIndicator appId={selectedProjectApp.appId} />
        )}

        {/* App layout modal for canvas assets (exclude ConfirmationCarousel — handled inline) */}
        <AppLayoutAssetModal
          show={
            isAppLayout &&
            nonConfirmationAssets.length > 0 &&
            appLayoutModalOpen
          }
          asset={
            nonConfirmationAssets.length > 0
              ? [...nonConfirmationAssets].sort(
                  (a, b) =>
                    new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime(),
                )[0]
              : null
          }
          threadId={effectiveThreadId}
          onClose={() => {
            setAppLayoutModalOpen(false);
            deleteCanvasAssets(assets.map((a) => a.id));
            // Clear asset_id from URL
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('asset_id');
            setSearchParams(newParams, { replace: true });
            // Close mobile canvas drawer when modal closes
            onMobileBack?.();
          }}
          updateThreadViewVisualDescription={updateThreadViewVisualDescription}
          container={canvasAssetsRef.current?.parentElement}
        />

        {/* Archive confirmation modal for project threads */}
        <ConfirmModal
          show={showArchiveConfirm}
          handleClose={() => setShowArchiveConfirm(false)}
          handleConfirm={handleArchiveProject}
          title='Archive project'
          bodyText="You're about to archive this project. It will be hidden from your project list. This action cannot be undone."
          confirmLabel={isArchiving ? 'Archiving...' : 'Archive'}
          cancelLabel='Cancel'
          confirmDisabled={isArchiving}
          singleButton={false}
          verticalButtons={false}
          littleSweetieVersion={false}
          confirmFirst={false}
        />
      </div>
    );
  },
);

CanvasAssets.displayName = 'CanvasAssets';

export default CanvasAssets;
